import { useState, useEffect, useRef } from "react";
import { IconButton } from "./btn";

interface Option {
  label: string;
  value: string;
}

interface MultiSelectDropdownProps {
  options?: Option[];
  placeholder?: string;
  className?: string;
  innerClassName?: string;
  onChange?: (selected: string[] | string) => void;
  label?: string;
  defaultValue?: string[] | string;
  defaultType?: "checkbox" | "radio";
  isOthersAddable?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options: initialOptions = [],
  placeholder = "Select options",
  className = "",
  onChange,
  label,
  defaultValue = [],
  defaultType = "checkbox",
  isOthersAddable,
  innerClassName,
}) => {
  type selectType = string[] | string;
  const [options, setOptions] = useState<Option[]>(initialOptions);
  const [selectedValues, setSelectedValues] =
    useState<selectType>(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  // const [inputValue, setInputValue] = useState(""); // NEW
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showOthersInput, setShowOthersInput] = useState(false);
  const [othersValue, setOthersValue] = useState("");
  const othersInputRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (showOthersInput && othersInputRef.current) {
      othersInputRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [showOthersInput]);

  const handleSelect = (value: string) => {
    if (value === "__others__") {
      setShowOthersInput(true);
      return;
    }
    setShowOthersInput(false);

    if (defaultType === "radio") {
      setSelectedValues(value);
    } else {
      setSelectedValues((prev) =>
        (prev as string[]).includes(value)
          ? (prev as string[]).filter((item) => item !== value)
          : [...(prev as string[]), value]
      );
    }
  };

  const handleAddOthersOption = () => {
    const trimmed = othersValue.trim();
    if (!trimmed) return;

    if (!options.find((opt) => opt.value === trimmed)) {
      const newOption = { label: trimmed, value: trimmed };
      setOptions((prev) => [...prev, newOption]);
    }

    handleSelect(trimmed);
    setOthersValue("");
    setShowOthersInput(false);
  };

  useEffect(() => {
    onChange?.(selectedValues);
  }, [selectedValues]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const mergedOptions = [
    ...options,
    ...(Array.isArray(selectedValues) ? selectedValues : [selectedValues])
      .filter((val) => !options.some((opt) => opt.value === val))
      .map((val) => ({ label: val, value: val })),
  ];

  return (
    <div className={`relative w-full ${className}`} ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-es-gray-200">{label}</label>
      )}
      <div
        className={`w-full p-3 ${
          innerClassName || "mt-2 main-border"
        } main-shadow rounded-lg flex justify-between items-center cursor-pointer bg-white`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="truncate">
          {defaultType === "checkbox"
            ? (selectedValues as string[]).join(", ") || placeholder
            : selectedValues || placeholder}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`w-5 h-5 transition-transform ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10">
          <ul className="max-h-48 overflow-y-auto p-2 space-y-2">
            {mergedOptions.length > 0 ? (
              mergedOptions.map((option) => (
                <li key={option.value} className="flex items-center space-x-2">
                  <input
                    type={defaultType}
                    id={option.value}
                    value={option.value}
                    checked={
                      defaultType === "radio"
                        ? selectedValues === option.value
                        : (selectedValues as string[]).includes(option.value)
                    }
                    onChange={() => {
                      handleSelect(option.value);
                      if (defaultType === "radio") setIsOpen(!isOpen);
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring focus:ring-blue-300"
                  />
                  <label
                    htmlFor={option.value}
                    className="text-gray-800 cursor-pointer"
                  >
                    {option.label}
                  </label>
                </li>
              ))
            ) : (
              <li className="text-gray-500 p-2 text-center">
                No options available
              </li>
            )}
            {isOthersAddable && (
              <>
                {/* Others Option */}
                <li className="flex items-center space-x-2">
                  <input
                    type={defaultType}
                    id="__others__"
                    value="__others__"
                    checked={showOthersInput}
                    onChange={() => handleSelect("__others__")}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring focus:ring-blue-300"
                  />
                  <label
                    htmlFor="__others__"
                    className="text-gray-800 cursor-pointer"
                  >
                    Others
                  </label>
                </li>

                {showOthersInput && (
                  <li className="p-1" ref={othersInputRef}>
                    <input
                      type="text"
                      value={othersValue}
                      onChange={(e) => setOthersValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddOthersOption();
                        }
                      }}
                      placeholder="Type and press Enter"
                      className="w-full p-2 text-sm border border-gray-300 rounded"
                    />
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;

interface FlowSelectDropdownProps {
  options?: any[];
  className?: string;
  onChange?: (selected: any) => void;
  actualModule?: any;
}

export const FlowSelectDropdown: React.FC<FlowSelectDropdownProps> = ({
  options = [],
  className = "",
  onChange,
  actualModule,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<"left" | "right">(
    "left"
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative w-auto ${className}`} ref={dropdownRef}>
      <div
        className="main-shadow items-center rounded-lg flex justify-center gap-4 cursor-pointer"
        onClick={() => {
          const rect = dropdownRef.current?.getBoundingClientRect();
          const windowWidth = window.innerWidth;
          if (rect) {
            const spaceRight = windowWidth - rect.right;
            setDropdownPosition(spaceRight < 220 ? "right" : "left"); // assumes dropdown min-width ~200px
          }
          setIsOpen(!isOpen);
        }}
      >
        <IconButton icon="famicons:filter" text="Filter" />
        {/* <span className="text-base font-medium text-[#3F516C]">
					{actualModule?.title}
				</span>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					className={`w-5 h-5 transition-transform ${
						isOpen ? "rotate-180" : "rotate-0"
					}`}
					viewBox="0 0 20 20"
					fill="currentColor">
					<path
						fillRule="evenodd"
						d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
						clipRule="evenodd"
					/>
				</svg> */}
      </div>

      {/* Dropdown List */}
      {isOpen && (
        <div
          className={`absolute ${
            dropdownPosition === "left" ? "left-0" : "right-0"
          } mt-2 w-full min-w-[200px] bg-white border border-gray-300 rounded-lg shadow-lg z-10`}
        >
          <ul className="max-h-48 overflow-y-auto p-2 space-y-2 text-sm">
            {options.length > 0 ? (
              options.map((option, i) => (
                <li key={i} className="w-full">
                  <button
                    type="button"
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-800 rounded-md"
                  >
                    {option?.title}
                  </button>
                </li>
              ))
            ) : (
              <li className="text-gray-500 p-2 text-center">
                No options available
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
